/* eslint-disable @typescript-eslint/camelcase */
import { Grid, makeStyles } from '@material-ui/core';
import { AccessAlarm, Settings, Subject } from '@material-ui/icons';
import DatePicker from 'components/DatePicker';
import TextEditor from 'components/TextEditor';
import ToggleCard from 'components/ToggleCard';
import i18n from 'i18n';
import React from 'react';
import GenericForm from 'skyone-lib/lib/GenericForm';
import clsx from 'clsx';
import { CompanyData, RolesEnum } from 'modules/authenticate/interfaces';
import { inAdminView, inSuperAdminView, inOperatorView } from 'utils/role';
import { isValid, parseISO, isAfter, isFuture } from 'date-fns';
import styles from '../styles';

const useStyles = makeStyles(styles);

export interface MaintenanceSchedule {
  enabled: boolean;
  start_datetime: string | null;
  end_datetime: string | null;
  message: string;
  terminate_sessions: boolean;
  terminate_instances: boolean;
}

export const scheduleInitialValues = {
  enabled: false,
  start_datetime: null,
  end_datetime: null,
  message: '',
  terminate_sessions: false,
  terminate_instances: false,
};

export interface Props {
  defaultValue: MaintenanceSchedule;
  companySelected: CompanyData | null;
  role?: RolesEnum;
  disabled?: boolean;
  onChange: (values: MaintenanceSchedule) => void;
  setHasError: React.Dispatch<React.SetStateAction<boolean>>;
}

const Form = ({
  defaultValue,
  role,
  companySelected,
  disabled,
  onChange,
  setHasError,
}: Props) => {
  const classes = useStyles();

  const [
    startDatetimeValue,
    setStartDatetimeValue,
  ] = React.useState<Date | null>(
    defaultValue?.start_datetime ? parseISO(defaultValue.start_datetime) : null
  );
  const [endDatetimeValue, setEndDatetimeValue] = React.useState<Date | null>(
    defaultValue?.end_datetime ? parseISO(defaultValue.end_datetime) : null
  );
  const [isStartDatetimeValid, setIsStartDatetimeValid] = React.useState(true);
  const [isEndDatetimeValid, setIsEndDatetimeValid] = React.useState(true);

  const [values, setValues] = React.useState<MaintenanceSchedule>(
    scheduleInitialValues
  );

  const handleChange = (
    values: MaintenanceSchedule,
    toggle: boolean,
    shouldChangeDefaultValues = true
  ) => {
    const newValues = { ...values, enabled: toggle };

    setValues(newValues);
    // eslint-disable-next-line no-unused-expressions
    shouldChangeDefaultValues && onChange(newValues);
  };

  React.useEffect(() => {
    if (defaultValue) {
      setValues(defaultValue);
    }
  }, [defaultValue]);

  React.useEffect(() => {
    setIsStartDatetimeValid(
      !!startDatetimeValue &&
        isValid(startDatetimeValue) &&
        isFuture(startDatetimeValue)
    );
    setIsEndDatetimeValid(
      !!endDatetimeValue &&
        !!startDatetimeValue &&
        isValid(endDatetimeValue) &&
        isAfter(endDatetimeValue, startDatetimeValue)
    );
  }, [startDatetimeValue, endDatetimeValue]);

  React.useEffect(() => {
    if (!values?.enabled) {
      setHasError(false);
    } else {
      setHasError(!isStartDatetimeValid || !isEndDatetimeValid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStartDatetimeValid, isEndDatetimeValid, values]);

  return (
    <ToggleCard
      id="toggle-scheduled-maintenance"
      active={values.enabled}
      getActive={(value: boolean) => handleChange(values, !value)}
      icon={
        <Settings
          className={clsx({
            [classes.iconGreen]: inAdminView(role, companySelected),
            [classes.iconGray]: inSuperAdminView(role, companySelected),
            [classes.iconBlue]: inOperatorView(role, companySelected),
            [classes.iconYellow]: companySelected,
          })}
        />
      }
      title={i18n.t('MAINTENANCE_SCHEDULED_KEY')}
      disable={disabled}
    >
      <Grid container className={classes.FormContainer}>
        <AccessAlarm className={classes.iconMaintenance} />
        <Grid item className={classes.datePicker}>
          <DatePicker
            id="schedule-start_datetime"
            type="datetime"
            label={i18n.t('START_ALERT_KEY')}
            keyValue="start_datetime"
            minDate={new Date()}
            value={values?.start_datetime}
            error={!isStartDatetimeValid}
            setFieldsValue={(key, value) => {
              handleChange(
                { ...values, [key]: value, end_datetime: null },
                values.enabled
              );
              setStartDatetimeValue(
                typeof value === 'string' ? parseISO(value) : (value as Date)
              );
            }}
          />
        </Grid>
        <Grid item className={classes.datePicker}>
          <DatePicker
            id="schedule-end_datetime"
            type="datetime"
            label={i18n.t('END_ALERT_KEY')}
            value={values?.end_datetime}
            error={!isEndDatetimeValid}
            minDate={startDatetimeValue}
            keyValue="end_datetime"
            setFieldsValue={(key, value) => {
              handleChange({ ...values, [key]: value }, values.enabled);
              setEndDatetimeValue(
                typeof value === 'string' ? parseISO(value) : (value as Date)
              );
            }}
          />
        </Grid>
        <GenericForm
          overrideClasses={{ checkboxLabel: { fontSize: 12, fontWeight: 500 } }}
          errors={[]}
          values={values}
          setFieldValue={(key, value) =>
            handleChange({ ...values, [key]: value }, values.enabled)
          }
          screenFields={[
            {
              gridXs: 12,
              label: i18n.t('TERMINATE_SESSIONS_KEY'),
              stateKey: 'terminate_sessions',
              isCheckbox: true,
            },
            {
              gridXs: 12,
              label: i18n.t('TERMINATE_INSTANCES_KEY'),
              stateKey: 'terminate_instances',
              isCheckbox: true,
            },
          ]}
        />

        <Grid item className={classes.textFieldContainer}>
          <Subject className={classes.subject} />
          <TextEditor
            initialValue={defaultValue?.message || ''}
            title={i18n.t('ADD_MESSAGE_MAINTENANCE_KEY')}
            getValue={value => {
              handleChange(
                { ...defaultValue, message: value },
                defaultValue.enabled
              );
            }}
            key={'message'}
            readOnly={disabled}
            maxLength={800}
          />
        </Grid>
      </Grid>
    </ToggleCard>
  );
};

export default Form;
